"use client";

import { Img } from "@/components/image";
import type { MobileAppBannerContentType } from "@/features/static-builder/types/mobile-app-banner.type";
import { useCheckPlatform } from "@/lib/hooks/use-check-platform";
import { cn } from "@/lib/utils";

type Props = {
  appBanner: MobileAppBannerContentType;
  iosImageUrl?: string | null;
  androidImageUrl?: string | null;
  variant: "default" | "listing" | "checkout" | "panel";
};

type LinkImage = {
  name: string;
  width: number;
  height: number;
};

const getLinkImage = (value: unknown): LinkImage | null => {
  if (!value || typeof value !== "object") return null;

  const mediaWithOptionalData = value as {
    data?: unknown;
    attributes?: unknown;
    name?: unknown;
    width?: unknown;
    height?: unknown;
  };

  const candidate =
    mediaWithOptionalData.data && typeof mediaWithOptionalData.data === "object"
      ? (mediaWithOptionalData.data as Record<string, unknown>)
      : mediaWithOptionalData;
  const source =
    candidate.attributes && typeof candidate.attributes === "object"
      ? (candidate.attributes as Record<string, unknown>)
      : candidate;

  return {
    name: typeof source.name === "string" ? source.name : "",
    width: typeof source.width === "number" ? source.width : 0,
    height: typeof source.height === "number" ? source.height : 0,
  };
};

export const AppBannerLinks = ({
  appBanner,
  iosImageUrl,
  androidImageUrl,
  variant,
}: Props) => {
  const platform = useCheckPlatform();

  const link =
    platform === "android" ? appBanner.androidLink : appBanner.iosLink;
  const imageUrl = platform === "android" ? androidImageUrl : iosImageUrl;
  const img =
    platform === "android"
      ? getLinkImage(appBanner.androidImage)
      : getLinkImage(appBanner.iosImage);

  if (!imageUrl || !img) {
    return null;
  }

  const widthClassName = cn(
    "w-42.5",
    variant === "listing" && "w-37.5",
    variant === "panel" && "w-33.75",
    variant === "checkout" && "w-30",
  );

  // Until the platform is known on the client, keep the badge's space empty,
  // so Android users don't see the App Store badge first and nothing shifts.
  if (!platform) {
    return (
      <span
        aria-hidden
        className={cn("z-10 block md:hidden", widthClassName)}
        style={{ aspectRatio: `${img.width} / ${img.height}` }}
      />
    );
  }

  return (
    <a
      className="z-10 md:hidden"
      href={link}
      rel="noopener noreferrer"
      target="_blank"
    >
      <Img
        alt={img.name}
        className={widthClassName}
        height={img.height}
        src={imageUrl}
        width={img.width}
      />
    </a>
  );
};
